import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { rememberVisitor } from "../../lib/metaEvent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/client`;
export const clientApi = axios.create({ baseURL: API, withCredentials: true });

// Turn FastAPI error payloads (string | [{msg}] | {msg}) into one friendly line.
export function apiError(err, fallback = "Something went wrong. Give it another go.") {
  const d = err?.response?.data?.detail;
  if (!d) return err?.message && err.message !== "Network Error" ? fallback : fallback;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || "").filter(Boolean).join(" ") || fallback;
  return d.msg || fallback;
}

const Ctx = createContext({ client: null, setClient: () => {}, refresh: async () => {}, logout: async () => {} });

// client: undefined = checking · null = signed out · object = signed in
export function ClientAuthProvider({ children }) {
  const [client, setClientState] = useState(undefined);
  const revision = useRef(0);
  const pendingRefresh = useRef(null);
  // An explicit sign-in/sign-out must win over an older bootstrap response.
  const setClient = useCallback((value) => {
    revision.current += 1; setClientState(value);
    if (value?.account_id) rememberVisitor({ email: value.email, phone: value.phone, firstName: value.first_name, lastName: value.last_name, accountId: value.account_id });
  }, []);

  const refresh = useCallback(() => {
    if (pendingRefresh.current) return pendingRefresh.current;
    const startedAt = revision.current;
    const commit = (data) => {
      if (revision.current === startedAt) setClientState(data);
      if (data?.account_id) rememberVisitor({ email: data.email, phone: data.phone, firstName: data.first_name, lastName: data.last_name, accountId: data.account_id });
      return data;
    };
    const request = (async () => {
      try { const { data } = await clientApi.get("/me"); return commit(data); }
      catch {
        if (revision.current !== startedAt) return null;
        try { const { data } = await clientApi.post("/auth/refresh"); return commit(data); }
        catch { return commit(null); }
      }
    })();
    pendingRefresh.current = request;
    request.finally(() => { if (pendingRefresh.current === request) pendingRefresh.current = null; });
    return request;
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // silently renew the 30-minute access cookie while the tab is open
  useEffect(() => {
    if (!client) return undefined;
    const id = setInterval(() => {
      const startedAt = revision.current;
      clientApi.post("/auth/refresh").catch(() => { if (revision.current === startedAt) setClient(null); });
    }, 20 * 60 * 1000);
    return () => clearInterval(id);
  }, [client, setClient]);

  const logout = useCallback(async () => { setClient(null); await clientApi.post("/auth/logout"); }, [setClient]);
  const value = useMemo(() => ({ client, setClient, refresh, logout }), [client, setClient, refresh, logout]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useClientAuth = () => useContext(Ctx);
